import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useIntl} from 'react-intl';
import styled from 'styled-components';
import {
	Alert,
	Button,
	Checkbox,
	ComboBox,
	DatePicker,
	EMPHASIS,
	Label,
	Text,
	TextArea,
	TextField,
	ToggleGroup,
} from '@forecast-it/design-system';
import Util from '../../../../shared/util/util';
import ProjectUtil from '../../../../shared/util/ProjectUtil';
import {hasPermission} from '../../../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../../../Permissions';
import {LabelDropdown} from '../../../../../components/dropdowns/LabelDropdown/LabelDropdown';
import AssignedDropdown from '../../../../shared/components/dropdowns/assigned-dropdown/assigned_dropdown';
import CreateTaskSetMutation from '../../../../../mutations/create_task_set_mutation';
import {createTaskSetMutation$data} from '../../../../../mutations/__generated__/createTaskSetMutation.graphql';
import {trackEvent} from '../../../../../tracking/amplitude/TrackingV2';
import {formatSetDate} from './describeTaskSet';
import {MAX_OCCURRENCES, partialFirstWeek, TaskSetFormState, useTaskSetPreview} from './useTaskSetPreview';
import {BODY_PADDING_Y, TaskSetDrawer, TaskSetDrawerProps} from './TaskSetDrawer';
import {HoursInput} from '../../../../shared/components/inputs/hours-input/hours_input_view';
import {createToast} from '../../../../shared/components/toasts/toast';

const WEEKDAYS = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];

/**
 * Minutes the company works on each weekday, keyed the way a JS date reports its
 * day: 0 is Sunday. Null or zero means the day is not worked, which is the same
 * signal the backend's deadline walker reads.
 */
export interface CompanyWorkingHours {
	monday?: number | null;
	tuesday?: number | null;
	wednesday?: number | null;
	thursday?: number | null;
	friday?: number | null;
	saturday?: number | null;
	sunday?: number | null;
}

/** Indexed by getUTCDay(), so Sunday leads. */
const WEEKDAY_KEYS: Array<keyof CompanyWorkingHours> = [
	'sunday',
	'monday',
	'tuesday',
	'wednesday',
	'thursday',
	'friday',
	'saturday',
];

/**
 * Joins day names the way the locale would: "Saturday or Sunday", and with three
 * of them "Wednesday, Saturday or Sunday". Disjunction rather than conjunction
 * because each date falls on one of the days, not all of them.
 *
 * Falls back to a plain comma join where Intl.ListFormat is missing, which keeps
 * the warning readable rather than dropping it.
 */
const joinDayNames = (names: string[], locale: string): string => {
	type ListFormatCtor = new (locale: string, options: {style: string; type: string}) => {format: (items: string[]) => string};
	const ListFormat = (Intl as unknown as {ListFormat?: ListFormatCtor}).ListFormat;
	if (names.length < 2) {
		return names[0] ?? '';
	}
	if (ListFormat) {
		return new ListFormat(locale, {style: 'long', type: 'disjunction'}).format(names);
	}
	return names.join(', ');
};

const Row = styled.div`
	display: flex;
	gap: 12px;

	> * {
		flex: 1;
		min-width: 0;
	}
`;

const Field = styled.div`
	display: flex;
	flex-direction: column;
	gap: 6px;
`;

/**
 * The design system's toggle group right-aligns its chips and forwards no
 * className to restyle it, so the wrapper re-aligns them. The chips sit flush
 * left under their label like every other control in the form.
 */
const WeekdayChips = styled.div`
	&& > * {
		justify-content: flex-start;
	}
`;

/**
 * 11px for the short field notes. The estimate's note is 12px in the design, so
 * it opts up rather than every hint growing to match one of them.
 */
const Hint = styled.div<{larger?: boolean}>`
	font-size: ${({larger}) => (larger ? '12px' : '11px')};
	font-weight: 350;
	color: #acacb9;
	line-height: ${({larger}) => (larger ? '17px' : '15px')};
`;

const Callout = styled.div`
	border: 1px solid #e6e6e6;
	background: #fafafb;
	border-radius: 5px;
	padding: 10px 12px;
	font-size: 12px;
	font-weight: 350;
	line-height: 17px;
	color: #4a4e56;
`;

/**
 * Amber, not red, and deliberately not an error style: the set may still be
 * created. Red would read as "you cannot proceed", which is the opposite of the
 * intent -- some companies genuinely do work weekends.
 */
const NonWorkingNotice = styled.div`
	background: #f7f4d9;
	border-left: 3px solid #e57700;
	border-radius: 3px;
	padding: 7px 10px;
	font-size: 12px;
	font-weight: 350;
	line-height: 17px;
	color: #4a4e56;
`;

const Preview = styled.div`
	border: 1px solid #e6e6e6;
	background: #fafafb;
	border-radius: 5px;
	padding: 11px 12px;
	display: flex;
	flex-direction: column;
	gap: 8px;
`;

const PreviewStats = styled.div`
	display: flex;
	gap: 26px;
	border-top: 1px solid #e6e6e6;
	padding-top: 9px;
`;

const Stat = styled.div`
	display: flex;
	flex-direction: column;
	gap: 2px;

	span {
		font-size: 9px;
		font-weight: 500;
		letter-spacing: 0.4px;
		text-transform: uppercase;
		color: #acacb9;
	}

	b {
		/* Monospace, per the design: these two numbers sit above each other and are
		   read by comparing them, so the digits have to line up. */
		font-family: 'DM Mono', ui-monospace, 'SFMono-Regular', Menlo, monospace;
		font-size: 15px;
		font-weight: 500;
		font-variant-numeric: tabular-nums;
	}
`;

/**
 * The embedded form's closing row: the host drawer owns the real footer slot, so
 * the buttons end the form instead, ruled off like the shell's own footer.
 */
const EmbeddedFooter = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	padding-top: 14px;
	border-top: 1px solid #e6e6e6;
	/* The host owns the real footer slot, and on the timeline it is left empty for
	   this tab, so these buttons live inside the scrolling body instead. Two
	   consequences to undo: with a long form -- an expanded date list on a
	   500-instance set -- they scrolled out of the viewport entirely, and with a
	   short one they floated up under the fields instead of sitting at the foot of
	   the panel. margin-top pushes them down when there is room; sticky keeps them
	   on screen when there is not. The background is needed because content now
	   scrolls underneath them. */
	position: sticky;
	bottom: -${BODY_PADDING_Y}px;
	margin-top: auto;
	background: #ffffff;
	/* A sticky row inside a padded scroller pins to the scroller's CONTENT box, so
	   it comes to rest one bottom-padding short of the visible edge and the form
	   scrolls through the strip underneath it. The negative offset moves the pin
	   down onto the visible edge, the padding fills the strip with the row's own
	   background, and the negative margin keeps the taller box from adding that
	   height back to the scroll length. */
	padding-bottom: ${BODY_PADDING_Y}px;
	margin-bottom: -${BODY_PADDING_Y}px;
`;

/**
 * Stands in for the drawer shell when the form is embedded in a host that
 * already provides one. Takes the shell's props so the render below needs no
 * branching beyond picking the shell; open state, title and Escape handling are
 * the host's.
 */
const EmbeddedShell = ({children, footer}: TaskSetDrawerProps) => (
	<>
		{children}
		{footer ? <EmbeddedFooter>{footer}</EmbeddedFooter> : null}
	</>
);

/**
 * The cap every other task-create path uses (`QuickAddTask`, `ProjectWorkflowAddTask`,
 * `ProjectSprintsAddTask`). Only `task_set.name` is actually VARCHAR(191) at this
 * width -- the task name becomes `task.name`, VARCHAR(300) -- so 191 here is about
 * matching every other place a task gets named, not the column task names land in.
 */
const NAME_MAX_LENGTH = 191;

const DEFAULT_FORM: TaskSetFormState = {
	frequency: 'WEEKLY',
	intervalLength: 1,
	weekdays: [],
	monthlyWeekday: null,
	monthlyMode: null,
	dayOfMonth: null,
	weekOrdinal: null,
	seedDate: '',
	boundType: 'COUNT',
	// One, so the count is always a choice: 12 pre-created a quarter of weekly
	// tasks for anyone who never looked at the field.
	boundCount: 1,
	boundEndDate: null,
};

/**
 * Cleared as well as seeded on every switch. Leaving weekdays populated when the
 * user moves to MONTHLY would send a stale weekly day to the backend, and leaving
 * monthlyWeekday null would have the preview compute against no day at all.
 */
/**
 * The day a stored ISO date falls on, or 1 when there is no usable date yet.
 *
 * A monthly rule has to fall on the day the user picked. Defaulting to the 1st
 * silently moved a set seeded 15 June onto 1 July, 1 August -- dates the user
 * never chose and nothing in the form explained.
 */
const dayOfMonthFrom = (iso: string): number => {
	const day = Number((iso || '').slice(8, 10));
	return Number.isInteger(day) && day >= 1 && day <= 31 ? day : 1;
};

/**
 * The weekday a stored ISO date falls on, for the monthly weekday modes.
 * Parsed as UTC so the day does not shift west of UTC.
 */
const weekdayFrom = (iso: string): string => {
	const parsed = new Date(`${iso}T00:00:00Z`);
	if (isNaN(parsed.getTime())) {
		return 'MONDAY';
	}
	return ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'][parsed.getUTCDay()];
};

/**
 * What each frequency needs cleared and seeded when the frequency itself changes.
 *
 * The monthly day is DERIVED from the seed date, so it is seeded here and follows
 * the date afterwards. The weekly day set is CHOSEN, so it is only ever cleared:
 * a set repeats on the days the user ticked, and nothing else may tick one for
 * them. Leaving stale values behind is what the clearing prevents -- a weekly day
 * carried into a MONTHLY rule would be sent to the backend unused, and a null
 * monthly mode would have the preview compute against no day at all.
 */
const patternDefaults = (frequency: TaskSetFormState['frequency'], seedDate: string): Partial<TaskSetFormState> => {
	const cleared = {weekdays: [], monthlyWeekday: null, monthlyMode: null, dayOfMonth: null, weekOrdinal: null};
	switch (frequency) {
		case 'MONTHLY':
			return {
				...cleared,
				monthlyMode: 'DAY_OF_MONTH',
				dayOfMonth: dayOfMonthFrom(seedDate),
				monthlyWeekday: weekdayFrom(seedDate),
				weekOrdinal: 1,
			};
		default:
			return cleared;
	}
};

/**
 * A whole, non-negative occurrence count, or the value already in the form when
 * the keystroke does not make one.
 *
 * Number('12x') is NaN, and NaN went back into the field as the literal string
 * "NaN" with the preview at zero instances and Create disabled with nothing
 * explaining why. A fractional count is refused the same way: a set produces a
 * whole number of tasks.
 */
/**
 * What a change of first-instance date carries with it.
 *
 * Only the monthly day, which is derived from that date: picking monthly on the
 * 15th and then moving the date to the 3rd should generate on the 3rd. Weekdays
 * are deliberately absent -- moving the date must not disturb the days the user
 * ticked, and must not pick one when none are.
 */
const monthlyDayForDate = (frequency: TaskSetFormState['frequency'], seedDate: string): Partial<TaskSetFormState> =>
	frequency === 'MONTHLY' ? {dayOfMonth: dayOfMonthFrom(seedDate), monthlyWeekday: weekdayFrom(seedDate)} : {};

const countFrom = (entered: string, current: number | null): number | null => {
	if (!entered.trim()) {
		return null;
	}
	const parsed = Number(entered);
	return Number.isInteger(parsed) && parsed >= 0 ? parsed : current;
};

export interface CreateTaskSetDrawerProps {
	/**
	 * Renders the form without its own drawer shell, for a host that already
	 * provides one -- the timeline's create drawer shows this form as a tab. The
	 * footer buttons then render as a row at the end of the form instead of on
	 * the shell, and isOpen/title handling belongs to the host.
	 */
	embedded?: boolean;
	projectId: string;
	/**
	 * Comes from whichever phase group's link was clicked. There is no phase field
	 * in this drawer because the group implies it. Null means the No Phase group,
	 * which is a supported case and not a missing value.
	 */
	phaseId?: string | null;
	projectName?: string;
	phaseName?: string | null;
	isOpen: boolean;
	/** Points projects take the number as-is; hours projects convert to minutes. */
	isPointsProject?: boolean;
	/**
	 * The attributes every instance is stamped with. These already sit on the
	 * scoping page's fragment, so this drawer widens no query.
	 */
	roles?: ReadonlyArray<{id: string; name?: string | null}>;
	assignablePersons?: ReadonlyArray<{id: string; firstName?: string | null; lastName?: string | null}>;
	statusColumns?: ReadonlyArray<{id: string; name?: string | null}>;
	/**
	 * The range the user dragged on the timeline, as ISO dates. The first
	 * instance starts on the drag's start, and the drag's end becomes the date
	 * the series is bounded by -- a drag across a range is already a statement of
	 * when the work should stop, so it seeds a date bound rather than a count.
	 * Absent on the scoping page, where creation does not start from a drag.
	 */
	seedDate?: string | null;
	boundEndDate?: string | null;
	/**
	 * Report a date the user changed here, for a host that shares the range with
	 * something else on screen. The timeline drawer puts the same range on its
	 * Task and Phase tabs, and the three are one statement of when the work runs,
	 * so an edit on any of them has to be the one that stands. Absent on the
	 * scoping page, where this form owns its dates outright.
	 */
	onSeedDateChange?: (value: string) => void;
	onBoundEndDateChange?: (value: string) => void;
	/**
	 * The company's task labels, in the shape the label dropdown reads. Both
	 * hosts already load these, so offering the field costs no extra query.
	 */
	labels?: ReadonlyArray<{
		id: string;
		name?: string | null;
		color?: string | null;
		category?: {id: string; name?: string | null} | null;
	}>;
	/**
	 * Decides whether the billing field is offered at all, together with the
	 * viewer's phase permission. Passed rather than derived so the drawer needs
	 * no project query of its own; the rule itself stays here so both hosts
	 * cannot drift apart on it.
	 */
	projectBudgetType?: string | null;
	/**
	 * The company's minutes per weekday, for the non-working-day warning. Absent
	 * simply means no warning is shown -- the check is advisory, so a host that
	 * has not loaded it must not be blocked from creating.
	 */
	companyWorkingHours?: CompanyWorkingHours | null;
	/**
	 * The hourly rate for a role on a given date, or null when the rate card
	 * prices neither the role nor a default on that date.
	 *
	 * A function of the date, not a flat rate table, because a rate card is
	 * versioned: each row carries a startDate and a role may have several. A set
	 * spanning a rate change therefore bills each instance at the rate in force
	 * on that instance's own date. The caller owns resolving that -- see
	 * RateCardUtil.getRoleRateOrDefaultOnDate, which also handles the card's
	 * default for a role it does not price.
	 *
	 * A null roleId is deliberate and supported: RateCardUtil.getRoleRateOrDefaultOnDate
	 * finds no per-role versions for it and falls through to the card's default rate,
	 * which is exactly what the budget does (financial-service files default rows under
	 * key 0, and SpecialRole.NO_ROLE IS 0). A role-less set is priced, not free.
	 */
	rateForRoleOnDate?: (roleId: string | null, date: string) => number | null;
	/**
	 * The RATE CARD's symbol, matching every other money figure on the scoping page.
	 *
	 * PIKA-672 briefly converted this preview into company base currency, on the
	 * reasoning that the budget stores base-currency numbers. That was the wrong
	 * anchor: the scoping page is a project-currency surface end to end -- its totals
	 * are fetched with `convertToProjectCurrency: true` and the totals header's
	 * PlannedRevenue row renders plannedRevenue with this same symbol. Converting only the drawer made
	 * it the odd one out, so the figure a user confirmed was still not the figure
	 * that visibly moved a few hundred pixels above. Whether this page should show
	 * base currency at all is a separate question for product.
	 */
	currencySymbol?: string;
	/**
	 * False for the budget types whose plannedRevenue does NOT accumulate from
	 * estimate x rate.
	 *
	 * NOT a fixed-price flag. Old FIXED_PRICE accrues exactly like T&M --
	 * CalculatePlannedFixedPriceOldFinancials passes isFixedPriceProject = false --
	 * so deriving this from "is fixed price" would hide a figure the budget really
	 * does move by.
	 *
	 * EVERY retainer is excluded, period budget type notwithstanding.
	 * FinancialLogic.ts:205 passes isFixedPriceProject = true literally on the
	 * non-V2 arm, and the V2 arm reaches calculatePlannedTimeMaterialFinancials with
	 * true as well, so defaultPeriodBudgetType never reaches the accrual at all.
	 * (An earlier revision of PIKA-672 made this conditional by reasoning from
	 * ProjectUtil.isFixedPriceProject -- that helper answers how the PAGE classifies
	 * a retainer, not whether addTaskPlanned accrues. Measured: a retainer task adds
	 * 1687.50 to billablePlannedTimeAndExpenses and 0 to plannedRevenue.)
	 *
	 * The exclusions arrive by two different routes, so neither a single flag nor a
	 * single gate explains them:
	 *   FIXED_PRICE_V2, every RETAINER  pass isFixedPriceProject = true
	 *   NON_BILLABLE                    passes FALSE, but TimeMaterialLogic's
	 *                                   `budgetType !== NON_BILLABLE` gate diverts
	 *                                   the price to
	 *                                   nonBillablePlannedTimeAndExpenses
	 */
	budgetMovesWithEstimate?: boolean;
	/**
	 * False when financialSourceSettings.plannedRevenue is ALLOCATION rather than
	 * TASK (TimeMaterialLogic.ts:143): no task of any kind moves planned revenue on
	 * such a project.
	 *
	 * Deliberately SEPARATE from budgetMovesWithEstimate even though both suppress
	 * the same figure. Folding them into one boolean meant one message had to cover
	 * both, and it told a Finance Manager on an allocation-sourced Time & Materials
	 * project that "this project's budget type does not add plan revenue" -- false
	 * about the budget type, and pointing at the wrong setting to change.
	 */
	revenueSourcedFromTasks?: boolean;
	/**
	 * False when the tenant hides the REVENUE feature, or the project is
	 * NON_BILLABLE -- the same predicate the page uses for its own plan-revenue
	 * column (ProjectUtil.projectTracksRevenue via ProjectScopingUtil's showRevenue).
	 *
	 * Feature hiding is client-side only: financial PERMISSION is enforced server
	 * side (ProjectType.rateCard goes through authoriseProjectFinancialValueResolver,
	 * so rateForRoleOnDate resolves to null without it), but nothing in this path
	 * consults the hidden-features list. Without this the drawer quotes Plan revenue
	 * and an hourly rate on a page where every money column has been removed.
	 *
	 * Kept separate from budgetMovesWithEstimate rather than folded in, because that
	 * prop selects a REASON to show and "your budget type does not accrue" is false
	 * on a T&M project whose revenue is merely hidden. This suppresses the figure,
	 * the reason and the rate hint together -- saying nothing instead of something
	 * untrue.
	 */
	revenueVisible?: boolean;
	/**
	 * Minutes per estimation point, for a points project.
	 *
	 * A points project DOES accrue planned revenue. The conversion happens at the
	 * service-company boundary -- SimpleTaskResponseUtil.setTaskEstimates writes
	 * forecastPoints x minutesPerEstimationPoint into estimateForecastMinutes -- so
	 * addTaskPlanned prices it off minutes with no points handling of its own. (The
	 * proof is the asymmetry with addTaskForecast, which DOES multiply, because
	 * timeLeft arrives unconverted and estimateForecastMinutes does not.)
	 *
	 * So the drawer must apply the same factor. An earlier revision of PIKA-672
	 * short-circuited points to no figure and then explained it with "points carry
	 * no rate" -- false, and the budget moved anyway.
	 */
	minutesPerEstimationPoint?: number | null;
	onClose: () => void;
	onCreated?: () => void;
}

/**
 * A single-select over a value that always has one, so it carries no empty entry.
 * Used for status: a task is always in some column, and the backend cannot store
 * the absence of one.
 */
const RequiredPicker = ({
	label,
	items,
	value,
	onChange,
	dataCy,
}: {
	label: string;
	items: ReadonlyArray<{id: string; name: string}>;
	value: string | null;
	onChange: (id: string) => void;
	dataCy: string;
}) => (
	<Field>
		<Label>{label}</Label>
		<ComboBox
			aria-label={label}
			data-cy={dataCy}
			defaultItems={items.map(i => ({id: i.id, name: i.name}))}
			value={value ?? ''}
			onChange={onChange}
			width="flex"
		>
			{item => (
				<ComboBox.Item textValue={item.name}>
					<Text noWrap>{item.name}</Text>
				</ComboBox.Item>
			)}
		</ComboBox>
	</Field>
);

/**
 * Wraps the two fields that use the task modal's own assignment control:
 * Assignees, and Role beside it. Called once each, people-only and role-only,
 * because that control takes people and roles independently -- which is how the
 * task modal and the Task tab both build the same pair, and why Role reads the
 * same here as it does there.
 *
 * Assignees are drawn as avatars rather than full-name chips, with a list
 * showing each person's avatar, name and role. A design-system multi-select was
 * tried first and rendered each person as a full-name chip, which wrapped onto a
 * second line as soon as two were picked.
 *
 * The avatar rules below are inert on the role-only call, which draws none.
 *
 * The label is supplied from outside: the control draws its own in grey
 * uppercase, and its avatars are positioned absolutely at an offset that assumes
 * that label is present, so the offset is corrected to centre them in the input.
 */
const AssignedField = styled(Field)`
	&& .assigned-dropdown-wrapper .assigned-icons {
		top: 7px;
	}

	/* The avatar is a 13.28px block with a 6.64px CSS-border triangle above and
	   below it, so it needs 26.56px, and the box it sits in is 26px and hides its
	   overflow -- half a pixel off the bottom point, which reads as a blunt
	   hexagon. The box clips for name truncation, and the name is hidden here, so
	   letting it show costs nothing. */
	&& .assigned-dropdown-wrapper .assigned-icons .forecast-person {
		overflow: visible;
	}

	/* The tab content this form sits in sets line-height: 24px, and the avatar's
	   initials carry none of their own, so the text box inherits 24px while the
	   hexagon's middle band is 13.28px. Anchored to the top of that band, the
	   letters land about 5px low, and the triangle drawn beneath the band paints
	   over them -- the initials are sheared off flat. Everywhere else in the app
	   this avatar inherits line-height: normal, so that is what it gets back. */
	&& .person-initials-hexagon-shape .initials {
		line-height: normal;
	}
`;

export const CreateTaskSetDrawer = ({
	embedded = false,
	projectId,
	phaseId,
	projectName,
	phaseName,
	isOpen,
	isPointsProject = false,
	roles = [],
	assignablePersons = [],
	statusColumns = [],
	seedDate: hostSeedDate,
	boundEndDate: hostBoundEndDate,
	onSeedDateChange,
	onBoundEndDateChange,
	companyWorkingHours,
	labels = [],
	projectBudgetType,
	rateForRoleOnDate,
	currencySymbol = '',
	budgetMovesWithEstimate = true,
	revenueSourcedFromTasks = true,
	revenueVisible = true,
	minutesPerEstimationPoint = null,
	onClose,
	onCreated,
}: CreateTaskSetDrawerProps) => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const [instanceName, setInstanceName] = useState('');
	const [setName, setSetName] = useState('');
	// Once the user edits the set name it stops tracking the task name.
	const [setNameEdited, setSetNameEdited] = useState(false);
	const [form, setForm] = useState<TaskSetFormState>(() =>
		hostSeedDate
			? {
					...DEFAULT_FORM,
					seedDate: hostSeedDate,
					...monthlyDayForDate(DEFAULT_FORM.frequency, hostSeedDate),
					// Only when the drag actually gave an end. Switching to a date
					// bound without one would open the form invalid, which is the
					// failure this is fixing.
					...(hostBoundEndDate ? {boundType: 'DATE' as const, boundEndDate: hostBoundEndDate} : {}),
			  }
			: DEFAULT_FORM
	);

	// A host that shares the range can have it changed somewhere this form cannot
	// see -- the timeline drawer's other two tabs edit the same dates. That
	// arrives as a prop, and has to land exactly as the user's own edit would,
	// monthly day derivation included. Guarded on equality so echoing the value
	// back after an edit made here is not a second render.
	useEffect(() => {
		if (!hostSeedDate) {
			return;
		}
		setForm(current =>
			current.seedDate === hostSeedDate
				? current
				: {...current, ...monthlyDayForDate(current.frequency, hostSeedDate), seedDate: hostSeedDate}
		);
	}, [hostSeedDate]);

	// Only while the series is bounded by a date. On a count bound there is no
	// field showing this, and the user has said the count is what ends the
	// series, so a date arriving from elsewhere has nothing to change.
	useEffect(() => {
		if (!hostBoundEndDate) {
			return;
		}
		setForm(current =>
			current.boundType !== 'DATE' || current.boundEndDate === hostBoundEndDate
				? current
				: {...current, boundEndDate: hostBoundEndDate}
		);
	}, [hostBoundEndDate]);
	// Drives the "Show all dates" expander. Collapsed by default: the range plus
	// the totals is the confirmation, the full list is on request.
	const [datesOpen, setDatesOpen] = useState(false);
	// Points only: held as typed, converted once at the boundary, because the
	// number IS the estimate and nothing may multiply it.
	const [estimateEntered, setEstimateEntered] = useState('');
	// Hours only: the hours input reports a parsed float on blur, so by the time
	// this is set it is already a number of hours -- "1h30m" arrives as 1.5.
	const [estimateHours, setEstimateHours] = useState<number | null>(null);
	// True when the hours input's own blur validation rejected the raw text (a
	// typo like "abc"). That state lives inside the input -- it shows a red
	// border, but nothing here could see it, so an unparseable estimate used to
	// create the set anyway with no estimate at all and no explanation why.
	const [estimateInvalid, setEstimateInvalid] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [roleId, setRoleId] = useState<string | null>(null);
	const [assignedPersonIds, setAssignedPersonIds] = useState<string[]>([]);
	// Opens on the project's first NAMED column, and cannot be cleared. Seeded from
	// the same filtered list the picker below renders -- statusColumns[0] alone
	// could be a column with no name, the exact case the filter exists to hide, and
	// would default to an id absent from the picker's own items: a blank required
	// field submitting a status the user never saw. A null status is not storable
	// either way: the backend guards against it because clearing would skip the
	// project's default column and the Jira/ADO first-column handling.
	const [statusColumnId, setStatusColumnId] = useState<string | null>(statusColumns.find(c => c.name)?.id ?? null);
	const [labelIds, setLabelIds] = useState<string[]>([]);
	// Held as the negative, because that is what the checkbox says. A set is
	// billable unless the user says otherwise, matching every other create path.
	const [nonBillable, setNonBillable] = useState(false);
	const [description, setDescription] = useState('');

	// Points: a typo must not become a number -- Number('2x') is NaN, and NaN
	// reached the preview as "NaNh" and made the ceiling check refuse a legal set.
	// Hours: the hours input has already parsed and validated, so its float only
	// needs converting to the minutes the column stores.
	const estimateParsed = ((): number | null => {
		if (!estimateEntered.trim()) {
			return null;
		}
		const parsed = Number(estimateEntered);
		return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
	})();
	const estimateForecast = isPointsProject ? estimateParsed : estimateHours === null ? null : Math.round(estimateHours * 60);

	const preview = useTaskSetPreview(form);

	const setSeedDate = (value: string) => {
		setForm({...form, ...monthlyDayForDate(form.frequency, value), seedDate: value});
		onSeedDateChange?.(value);
	};

	/**
	 * The notice for a weekly set whose first week holds fewer than every selected
	 * day. Informational only: the set is still creatable as it stands, and the
	 * action is a shortcut, not a fix the user is required to take. Recomputed from
	 * the form on every render, so it clears itself the moment Starts, Pattern or
	 * Repeats on stop producing a partial week.
	 */
	const partialWeek = useMemo(() => {
		const partial = partialFirstWeek(form);
		if (!partial) {
			return null;
		}
		const dayMonth = (iso: string) => formatSetDate(intl, iso, {weekday: 'short', day: 'numeric', month: 'short'});
		const start = dayMonth(partial.seedDate);
		if (!start) {
			return null;
		}
		const days = partial.daysInFirstWeek.map(day => intl.formatMessage({id: `common.${day.toLowerCase()}.short`}));
		// One message, so a translation can reorder or merge the count clause rather
		// than having it glued on after the first sentence.
		const countBound = form.boundType === 'COUNT' && !!form.boundCount;
		const text = intl.formatMessage(
			{id: 'task_set.partial_first_week'},
			{
				start,
				days: days.join(', '),
				countBound: countBound ? 'yes' : 'no',
				count: form.boundCount ?? 0,
			}
		);
		// No shortcut when an end date falls before the next full week: taking it
		// would leave a set with no instances.
		const fullWeekStart = partial.fullWeekStart;
		const fullWeekLabel = fullWeekStart ? dayMonth(fullWeekStart) : null;
		return {
			text,
			action: fullWeekLabel
				? intl.formatMessage({id: 'task_set.partial_first_week_action'}, {date: fullWeekLabel})
				: null,
			fullWeekStart,
		};
	}, [form, intl]);

	/**
	 * Which of the dates about to be created fall on a day the company does not
	 * work, and which days those are.
	 *
	 * Checked against every generated date rather than the Starts field alone: a
	 * set can start on a Monday and recur every Saturday, and that has to warn.
	 * The list is the one the preview already built for "Show all dates", so this
	 * is a filter over existing work rather than a second expansion.
	 *
	 * Dates are parsed as UTC to match how the preview produced them; parsing as
	 * local time shifts the weekday west of UTC and would warn about the wrong
	 * day.
	 */
	const nonWorkingDays = (() => {
		if (!companyWorkingHours) {
			return null;
		}
		const affectedDayIndexes = new Set<number>();
		let affected = 0;
		for (const date of preview.dates) {
			const parsed = new Date(`${date}T00:00:00Z`);
			if (isNaN(parsed.getTime())) {
				continue;
			}
			const dayIndex = parsed.getUTCDay();
			const minutes = companyWorkingHours[WEEKDAY_KEYS[dayIndex]];
			if (!minutes) {
				affected++;
				affectedDayIndexes.add(dayIndex);
			}
		}
		if (affected === 0) {
			return null;
		}
		// Named in week order starting Monday, so "Saturday or Sunday" rather than
		// the order the dates happened to fall in.
		const ordered = [1, 2, 3, 4, 5, 6, 0].filter(index => affectedDayIndexes.has(index));
		const names = ordered.map(index => formatMessage({id: `common.${WEEKDAY_KEYS[index]}`}));
		return {affected, total: preview.dates.length, names};
	})();

	// The project has revenue to track, and the viewer may change what a phase
	// bills. The task modal gates its own billable flag on those two plus the
	// permission to edit a task -- which it needs because toggling billable
	// THERE edits a task that already exists. Nothing is being edited here, and
	// who may create at all is already settled before this form opens, so that
	// third condition has nothing to say about a create surface.
	const showBillable =
		ProjectUtil.projectTracksRevenue({budgetType: projectBudgetType} as never) &&
		hasPermission(PERMISSION_TYPE.PHASE_UPDATE);

	// The rate quoted beside the role picker: the one in force on the first
	// instance, since that is the date the reader can see.
	//
	// Null ONLY when the card prices neither the role nor a default on that date --
	// no longer when a role is simply unchosen. The lookup resolves the card default
	// for a null roleId, which is what the budget does too, so the hint is absent
	// rather than inventing a number, and absent stays distinct from a zero, which
	// is a real rate.
	const firstInstanceDate = preview.dates[0] ?? form.seedDate;

	// No currency conversion here, deliberately. Every money figure on the scoping
	// page is in RATE CARD currency, so this one is too -- see currencySymbol's doc
	// comment for why an earlier version of PIKA-672 converted and why that was
	// wrong.
	const selectedRoleRate = rateForRoleOnDate && firstInstanceDate ? rateForRoleOnDate(roleId, firstInstanceDate) : null;

	// Priced per instance rather than by one rate times the count, because the
	// rate can change part-way through a set: a card whose rate rises next month
	// bills the instances before and after it differently. Summed over the dates
	// the preview will actually create, so an over-ceiling rule -- which is
	// refused anyway -- cannot inflate the figure.
	//
	// Hours only. A points estimate has no hours to price, so there is no revenue
	// to state rather than a zero that reads as "free".
	const planRevenue = (() => {
		// No `!roleId` guard. A role-less set IS priced, at the card's default-role
		// rate -- refusing here showed nothing while the budget moved by
		// defaultRate x estimate x N, which is the worst way for the preview and the
		// budget to disagree. Let the lookup answer; `priced` still covers a card
		// that prices nothing.
		//
		// budgetMovesWithEstimate is false for FIXED_PRICE_V2, every RETAINER and
		// NON_BILLABLE, whose plannedRevenue does not accumulate from estimates at
		// all -- a figure there would be confidently wrong rather than merely absent.
		// revenueVisible is the feature-hiding half: say nothing at all, rather than
		// give a reason, when the page itself is not showing revenue.
		if (
			!revenueVisible ||
			!budgetMovesWithEstimate ||
			!revenueSourcedFromTasks ||
			!estimateForecast ||
			!rateForRoleOnDate
		) {
			return null;
		}
		// estimateForecast is POINTS on a points project and minutes otherwise
		// (see its derivation above). The budget converts points at
		// minutesPerEstimationPoint before pricing, so do the same here rather than
		// refusing to price -- a points project accrues plan revenue like any other.
		// Without the factor there is nothing to convert with, so stay silent.
		const minutesPerInstance = isPointsProject
			? minutesPerEstimationPoint
				? estimateForecast * minutesPerEstimationPoint
				: null
			: estimateForecast;
		if (minutesPerInstance === null) {
			return null;
		}
		const hoursPerInstance = minutesPerInstance / 60;
		let total = 0;
		let priced = false;
		for (const date of preview.dates) {
			const rate = rateForRoleOnDate(roleId, date);
			if (rate !== null) {
				total += hoursPerInstance * rate;
				priced = true;
			}
		}
		return priced ? total : null;
	})();

	// What is submitted. Falls back to the task name whenever `setName` is blank --
	// not only before the first blur (typing a name and going straight to Create,
	// where the mirror has not fired yet), but also if the user edits the field and
	// then clears it. Gating the fallback on `setNameEdited` used to make the
	// second case a dead end: once edited, `setName` alone decided the value, so an
	// emptied field left `effectiveSetName` empty, Create silently disabled, and
	// nothing on screen explaining why -- re-blurring the task name would not
	// restore it either, since the mirror had already latched off.
	const effectiveSetName = setName || instanceName;

	// Copied on blur rather than per keystroke. Following each keystroke means
	// watching the second field change while typing in the first, which reads as a
	// glitch; on blur it reads as an auto-fill.
	const onInstanceNameBlur = () => {
		if (!setNameEdited) {
			setSetName(instanceName);
		}
	};

	// Fires on the transition into the over-ceiling state, not on every render.
	// Without the ref this reports once per keystroke and the numbers are useless.
	const reportedOverCeiling = useRef(false);
	useEffect(() => {
		if (preview.overCeiling && !reportedOverCeiling.current) {
			reportedOverCeiling.current = true;
			// A weekday count rather than the days themselves: the question this has
			// to answer is whether every-weekday patterns are what hit the wall.
			trackEvent('Task Set', 'Blocked By Instance Ceiling', {
				requestedCount: preview.count,
				ceiling: MAX_OCCURRENCES,
				frequency: form.frequency,
				intervalLength: form.intervalLength,
				weekdayCount: form.weekdays.length,
				boundType: form.boundType,
			});
		}
		if (!preview.overCeiling) {
			reportedOverCeiling.current = false;
		}
	}, [preview.overCeiling, preview.count, form]);

	const weekdaysMissing = form.frequency === 'WEEKLY' && form.weekdays.length === 0;
	const canCreate =
		!submitting &&
		Boolean(instanceName) &&
		Boolean(effectiveSetName) &&
		Boolean(form.seedDate) &&
		!weekdaysMissing &&
		!estimateInvalid &&
		preview.count > 0 &&
		!preview.overCeiling;

	const onCreate = () => {
		setSubmitting(true);
		Util.CommitMutation(
			CreateTaskSetMutation,
			{
				globalProjectId: projectId,
				globalPhaseId: phaseId ?? null,
				frequency: form.frequency,
				intervalLength: form.intervalLength,
				weekdays: form.weekdays,
				monthlyWeekday: form.monthlyWeekday,
				monthlyMode: form.monthlyMode,
				dayOfMonth: form.dayOfMonth,
				weekOrdinal: form.weekOrdinal,
				seedDate: form.seedDate,
				boundType: form.boundType,
				// Only the bound that applies. The form keeps both halves alive so
				// switching between them does not lose what was typed, but storing the
				// unused one leaves a set claiming a bound it does not have: a
				// date-bounded set kept whatever count the form happened to hold, and
				// the instance chip read that as the length of the series -- "1 of 1"
				// on the first task and no label at all on the rest.
				boundCount: form.boundType === 'COUNT' ? form.boundCount : null,
				boundEndDate: form.boundType === 'DATE' ? form.boundEndDate : null,
				setName: effectiveSetName,
				instanceName,
				estimateForecast: estimateForecast,
				globalRoleId: roleId,
				globalAssignedPersonIds: assignedPersonIds,
				globalStatusColumnId: statusColumnId,
				globalLabelIds: labelIds,
				// Sent only when the box is ticked. Omitting it leaves the phase's own
				// billable default in force, which is what an untouched checkbox
				// should mean -- sending true instead would override that default
				// every time, and the field is hidden altogether on a project with no
				// revenue to track.
				billable: nonBillable ? false : undefined,
				description: description.trim() || null,
			},
			(response: unknown) => {
				setSubmitting(false);
				trackEvent('Task Set', 'Created', {frequency: form.frequency, instances: preview.count});
				// The scheduling canvas draws from its own data rather than the Relay
				// store, so it only learns about these instances if it is told. The
				// event carries a from-scheduling marker that is only set on those
				// pages, so this is inert on the scoping page, which refetches through
				// onCreated instead.
				Util.dispatchScheduleEvent(response);
				/* Generation can stop part way. The set and the instances made so far
				   exist, so this is a success with a warning, not a failure: closing as
				   normal shows the reader the set, and the list flags it until someone
				   extends it. The total is the same sum for a date set and a count
				   set, so one sentence covers both. */
				const shortfall = (response as createTaskSetMutation$data)?.createTaskSet?.taskSet?.generationShortfall;
				if (shortfall) {
					const created = shortfall.createdCount ?? 0;
					createToast({
						warning: true,
						duration: 10000,
						message: formatMessage(
							{id: 'task_set.shortfall.created_toast'},
							{created, total: created + (shortfall.remainingCount ?? 0)}
						),
					});
				}
				onCreated?.();
				onClose();
			},
			true,
			null,
			// Without this the drawer stays submitting for ever and Create is dead,
			// with no way out but Escape. The fallback keeps the standard error modal.
			//
			// The fallback returns true when it has scheduled its own retry, meaning
			// this attempt is not over. Re-enabling Create then would let a second
			// create fire while the first is still in flight, so the form stays
			// submitting until an attempt actually finishes.
			(transaction: unknown, onFailure?: (t: unknown) => boolean | void) => {
				const retryInFlight = onFailure ? onFailure(transaction) === true : false;
				if (!retryInFlight) {
					setSubmitting(false);
				}
			}
		);
	};

	const subtitle = [projectName, phaseName].filter(Boolean).join(' · ') || undefined;

	const previewMessage = preview.overCeiling
		? formatMessage(
				{
					id: preview.countIsAtProbeLimit ? 'task_set.too_many_instances_unbounded' : 'task_set.too_many_instances',
				},
				{count: preview.count, max: MAX_OCCURRENCES}
		  )
		: formatMessage({id: 'task_set.preview_count'}, {count: preview.count});

	const Shell = embedded ? EmbeddedShell : TaskSetDrawer;

	return (
		<Shell
			isOpen={isOpen}
			onClose={onClose}
			title={formatMessage({id: 'task_set.title'})}
			subtitle={subtitle}
			footer={
				<>
					<Button emphasis={EMPHASIS.MEDIUM} onPress={onClose} data-cy="task-set-cancel">
						{formatMessage({id: 'common.cancel'})}
					</Button>
					{/* Creation makes one task per instance, so a bounded set of twenty-six is
					    a visibly long wait. waiting is the design system's own affordance:
					    it swaps the label for a loader and disables the button, so the only
					    feedback is no longer the label going grey. */}
					<Button
						emphasis={EMPHASIS.HIGH}
						disabled={!canCreate}
						waiting={submitting}
						onPress={onCreate}
						data-cy="task-set-create"
					>
						{formatMessage({id: 'task_set.create'})}
					</Button>
				</>
			}
		>
			<Row>
				<TextField
					label={formatMessage({id: 'task_set.instance_name'})}
					value={instanceName}
					onValueChange={setInstanceName}
					onBlur={onInstanceNameBlur}
					maxLength={NAME_MAX_LENGTH}
					helperText={
						instanceName.length >= NAME_MAX_LENGTH
							? formatMessage({id: 'task_set.name_at_limit'}, {max: NAME_MAX_LENGTH})
							: undefined
					}
					width="flex"
				/>
				<TextField
					label={formatMessage({id: 'task_set.set_name'})}
					value={setName}
					onValueChange={(value: string) => {
						setSetNameEdited(true);
						setSetName(value);
					}}
					maxLength={NAME_MAX_LENGTH}
					helperText={
						setName.length >= NAME_MAX_LENGTH
							? formatMessage({id: 'task_set.name_at_limit'}, {max: NAME_MAX_LENGTH})
							: undefined
					}
					width="flex"
				/>
			</Row>

			<Row>
				{/* Three lines tall: the one field in the form whose value is prose. The
				    design system's textarea reports the value, not the event.
				    Its data-cy lands on both the wrapper div and the inner <textarea>,
				    so [data-cy="task-set-description"] matches two elements -- a
				    Playwright locator on it needs textarea[data-cy=...] to pick the
				    field itself. Not something this call site can fix; the duplication
				    is in the component. */}
				<TextArea
					label={formatMessage({id: 'common.description'})}
					value={description}
					isOptional
					height={76}
					onChange={(value: string) => setDescription(value)}
					onBlur={() => undefined}
					data-cy="task-set-description"
				/>
			</Row>

			<Row>
				{isPointsProject ? (
					<TextField
						label={formatMessage({id: 'task_set.estimate'})}
						value={estimateEntered}
						onValueChange={setEstimateEntered}
						width="flex"
					/>
				) : (
					<Field>
						<Label>{formatMessage({id: 'task_set.estimate'})}</Label>
						{/* The app's hours input, in its design-system skin: right-aligned,
						    the unit written into the value, and "1h30m" understood. It
						    reports on blur, so the preview recomputes when the user leaves
						    the field. The label is ours so it matches the row's other
						    labels rather than the input's own v2 one. */}
						<HoursInput
							replicateDesignSystem
							width="100%"
							placeholder={`0${formatMessage({id: 'common.hours.short'})}`}
							value={estimateHours ?? 0}
							mutation={(value: unknown) => {
								const isEmpty = value === null || value === undefined || value === '';
								const isValidNumber = typeof value === 'number' && Number.isFinite(value);
								// Neither: the input's own blur validation rejected the raw text
								// (a typo like "abc"). It already shows this as a red border;
								// estimateInvalid is what lets canCreate see it too, so the set
								// is not created silently with no estimate at all.
								setEstimateInvalid(!isEmpty && !isValidNumber);
								if (isValidNumber) {
									setEstimateHours(value > 0 ? value : null);
								} else if (isEmpty) {
									setEstimateHours(null);
								}
							}}
							cy="task-set-estimate-hours"
						/>
						{estimateInvalid ? <Hint>{formatMessage({id: 'task_set.estimate_invalid'})}</Hint> : null}
					</Field>
				)}
				<AssignedField>
					<Label>{formatMessage({id: 'common.role'})}</Label>
					<AssignedDropdown
						assignableRoles={roles.filter(r => r.name).map(r => ({node: {id: r.id, name: r.name as string}}))}
						assignedRole={roles.find(r => r.id === roleId) ?? null}
						assignRole={(roleIdToAssign: string | null) => setRoleId(roleIdToAssign || null)}
						isMultiSelect={false}
						showSuggestions={false}
						closeOnChoice={true}
						cy="task-set-role"
					/>
				</AssignedField>
			</Row>
			{/* Only true on an hours project. A points estimate does not drive the
			    deadline -- a points instance keeps deadline = start -- so claiming it
			    does would be worse than saying nothing. */}
			{isPointsProject ? null : <Hint larger>{formatMessage({id: 'task_set.estimate_help'})}</Hint>}
			{/* The rate is what makes the plan-revenue figure below legible: without it
			    the number appears from nowhere.
			    Shown whenever the rate card prices SOMETHING on that date -- including
			    before a role is chosen, where the lookup resolves the card's default.
			    That is deliberate (PIKA-672): a role-less set is priced at the default
			    rate and moves the budget, so hiding the rate until a role is picked
			    left the figure below unexplained. Absent only when the card prices
			    neither the role nor a default. */}
			{/* Also hidden wherever the preview states there is NO plan revenue. This
			    hint exists to make that figure legible; quoting a live per-hour rate
			    directly above "this project is estimated in points, which carry no
			    rate" is a contradiction, and since the role guard was dropped it would
			    be the drawer's state on open rather than something the user had to
			    reach. */}
			{/* Gated on the things that suppress revenue ENTIRELY, not on whether a
			    figure happens to exist yet. Tying it to planRevenue would withhold the
			    rate until an estimate had been typed, which is exactly when a user
			    wants to see it. isPointsProject is no longer a condition: a points
			    project is priced, and its rate is per hour like any other. */}
			{selectedRoleRate === null || !revenueVisible || !budgetMovesWithEstimate || !revenueSourcedFromTasks ? null : (
				<Hint>{formatMessage({id: 'task_set.role_rate'}, {rate: `${currencySymbol}${selectedRoleRate}`})}</Hint>
			)}

			<Row>
				<AssignedField>
					<Label>{formatMessage({id: 'common.assignees'})}</Label>
					<AssignedDropdown
						assignablePersons={assignablePersons.map(person => ({node: {person}}))}
						assignedPersons={assignablePersons.filter(person => assignedPersonIds.includes(person.id))}
						assignPerson={(personIds: string[]) => setAssignedPersonIds(personIds)}
						unassignPerson={(personId: string) =>
							setAssignedPersonIds(assignedPersonIds.filter(id => id !== personId))
						}
						isMultiSelect={true}
						showSuggestions={false}
						cy="task-set-assignee"
					/>
				</AssignedField>
				{/* A host without the project's status columns (the timeline drawer)
				    offers no picker rather than an empty one; the backend then applies
				    the project's default column exactly as ordinary task creation
				    does. */}
				{statusColumns.length === 0 ? null : (
					<RequiredPicker
						label={formatMessage({id: 'common.status'})}
						items={statusColumns.filter(c => c.name).map(c => ({id: c.id, name: c.name as string}))}
						value={statusColumnId}
						onChange={setStatusColumnId}
						dataCy="task-set-status"
					/>
				)}
			</Row>

			<Row>
				<Field>
					<Label>{formatMessage({id: 'common.labels'})}</Label>
					{/* The task modal's own label control, given plain objects rather than
					    a Relay fragment: both hosts already hold the company's labels, so
					    the named export is used instead of the fragment container. Every
					    instance is stamped with whatever is chosen here. No machine
					    suggestions -- a set is labelled before any instance exists, so
					    there is nothing yet to suggest from. */}
					<LabelDropdown
						height="large"
						labels={labels.slice() as never}
						selectedItems={labelIds}
						setSelectedItems={setLabelIds}
						data-cy="task-set-labels"
					/>
				</Field>
			</Row>

			{/* Hidden outright rather than disabled where the project tracks no
			    revenue or the viewer may not change phases: there is no billing
			    decision to take, the same way the status picker is absent when a host
			    offers no columns. */}
			{showBillable ? (
				<Row>
					<Checkbox
						checked={nonBillable}
						onChange={checked => setNonBillable(checked === true)}
						labelText={formatMessage({id: 'task_set.non_billable'})}
						notBoldLabel
						aria-label={formatMessage({id: 'task_set.non_billable'})}
						data-cy="task-set-non-billable"
					/>
				</Row>
			) : null}

			<Row>
				<DatePicker
					label={formatMessage({id: 'task_set.first_instance'})}
					value={form.seedDate}
					onChange={setSeedDate}
				/>
				<Field>
					<Label>{formatMessage({id: 'task_set.pattern'})}</Label>
					<ComboBox
						aria-label={formatMessage({id: 'task_set.pattern'})}
						defaultItems={(['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'] as const).map(value => ({
							id: value,
							name: formatMessage({id: `task_set.frequency_${value.toLowerCase()}`}),
						}))}
						value={form.frequency}
						onChange={(value: TaskSetFormState['frequency']) =>
							setForm({...form, ...patternDefaults(value, form.seedDate), frequency: value})
						}
						width="flex"
					>
						{item => (
							<ComboBox.Item textValue={item.name}>
								<Text noWrap>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</Field>
			</Row>

			{form.frequency === 'WEEKLY' ? (
				<Field>
					<Label>{formatMessage({id: 'task_set.weekdays'})}</Label>
					{/* Seven chips rather than a select: a weekly rule carries a SET of
					    days, and this is the control the existing repeat modal uses.
					    Reuses the short weekday keys, which are "Mon", "Tue" and so on
					    -- unambiguous, unlike single letters. */}
					<WeekdayChips>
						<ToggleGroup
							type="multiple"
							value={form.weekdays}
							onValueChange={(value: string | string[]) =>
								setForm({...form, weekdays: Array.isArray(value) ? value : [value]})
							}
							options={WEEKDAYS.map(day => ({
								value: day,
								label: formatMessage({id: `common.${day.toLowerCase()}.short`}),
								disabled: false,
							}))}
						/>
					</WeekdayChips>
					{weekdaysMissing ? <Hint>{formatMessage({id: 'task_set.weekdays_required'})}</Hint> : null}
				</Field>
			) : null}

			<Row>
				<Field>
					<Label>{formatMessage({id: 'task_set.ends'})}</Label>
					<ComboBox
						aria-label={formatMessage({id: 'task_set.ends'})}
						defaultItems={[
							{id: 'COUNT', name: formatMessage({id: 'task_set.ends_after'})},
							{id: 'DATE', name: formatMessage({id: 'task_set.ends_on_date'})},
						]}
						value={form.boundType}
						onChange={(value: TaskSetFormState['boundType']) => setForm({...form, boundType: value})}
						width="flex"
					>
						{item => (
							<ComboBox.Item textValue={item.name}>
								<Text noWrap>{item.name}</Text>
							</ComboBox.Item>
						)}
					</ComboBox>
				</Field>
				{form.boundType === 'COUNT' ? (
					<TextField
						label={formatMessage({id: 'task_set.ends_occurrences'})}
						value={String(form.boundCount ?? '')}
						onValueChange={(value: string) => setForm({...form, boundCount: countFrom(value, form.boundCount)})}
						width="flex"
					/>
				) : (
					<DatePicker
						label={formatMessage({id: 'task_set.ends_on_date'})}
						value={form.boundEndDate ?? ''}
						onChange={(value: string) => {
							setForm({...form, boundEndDate: value});
							onBoundEndDateChange?.(value);
						}}
					/>
				)}
			</Row>

			{partialWeek ? (
				<div data-cy="task-set-partial-first-week">
					<Alert type={Alert.TYPE.INFORMATION} text={partialWeek.text}>
						{partialWeek.fullWeekStart && partialWeek.action ? (
							<Button
								emphasis={EMPHASIS.LOW}
								onPress={() => partialWeek.fullWeekStart && setSeedDate(partialWeek.fullWeekStart)}
								data-cy="task-set-partial-first-week-action"
							>
								{partialWeek.action}
							</Button>
						) : null}
					</Alert>
				</div>
			) : null}

			<Callout>{formatMessage({id: 'task_set.in_advance_help'})}</Callout>

			<Preview>
				<Text>{previewMessage}</Text>
				{/* Directly under the count, because it qualifies that count -- not in
				    the explainer above, which is static copy about how sets work and
				    should not start carrying conditional content. Not dismissible: it
				    recomputes as the rule changes, and a dismissible warning that also
				    updates live leaves no good answer for whether dismissing should
				    suppress a later, different one. */}
				{nonWorkingDays ? (
					<NonWorkingNotice data-cy="task-set-non-working-days">
						{formatMessage(
							{
								id:
									nonWorkingDays.names.length === 1
										? 'task_set.non_working_days_one'
										: 'task_set.non_working_days_many',
							},
							{
								affected: nonWorkingDays.affected,
								total: nonWorkingDays.total,
								days: joinDayNames(nonWorkingDays.names, intl.locale),
							}
						)}
					</NonWorkingNotice>
				) : null}
				{preview.dates.length > 0 ? (
					<>
						<Text>
							{formatMessage(
								{id: 'task_set.preview_range'},
								{
									first: formatSetDate(intl, preview.dates[0]) ?? preview.dates[0],
									last:
										formatSetDate(intl, preview.dates[preview.dates.length - 1]) ??
										preview.dates[preview.dates.length - 1],
								}
							)}
						</Text>
						<Button emphasis={EMPHASIS.LOW} onPress={() => setDatesOpen(!datesOpen)}>
							{formatMessage({id: datesOpen ? 'task_set.hide_dates' : 'task_set.show_all_dates'})}
						</Button>
						{datesOpen
							? preview.dates.map(date => <Text key={date}>{formatSetDate(intl, date) ?? date}</Text>)
							: null}
						<PreviewStats>
							{estimateForecast ? (
								<Stat>
									<span>{formatMessage({id: 'task_set.total_estimate'})}</span>
									{/* estimateForecast is minutes on an hours project and points on a
									    points project, so the total is divided and suffixed to match
									    rather than always reading as hours. */}
									<b>
										{isPointsProject
											? `${preview.count * estimateForecast}${formatMessage({id: 'common.points.short'})}`
											: `${(preview.count * estimateForecast) / 60}${formatMessage({
													id: 'common.hours.short',
											  })}`}
									</b>
								</Stat>
							) : null}
							{planRevenue !== null ? (
								<Stat data-cy="task-set-plan-revenue">
									<span>{formatMessage({id: 'task_set.plan_revenue'})}</span>
									<b>{`${currencySymbol}${Math.round(planRevenue)}`}</b>
								</Stat>
							) : revenueVisible && (!budgetMovesWithEstimate || !revenueSourcedFromTasks) ? (
								// Two causes, two messages -- they point at different settings,
								// so one string cannot serve them. Silent rather than explained
								// in every other case: a card pricing nothing, a points project
								// missing its conversion factor, and revenue hidden at tenant
								// level, where naming a cause would be naming a wrong one. A
								// role-less set is PRICED at the card default, so it is not
								// here either.
								<Hint data-cy="task-set-no-revenue-reason">
									{formatMessage({
										id: !budgetMovesWithEstimate
											? 'task_set.no_revenue_budget_type'
											: 'task_set.no_revenue_allocation',
									})}
								</Hint>
							) : null}
						</PreviewStats>
					</>
				) : null}
			</Preview>
		</Shell>
	);
};
